import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProcessWorkstation from './components/ProcessWorkstation';
import ProcessTimeline from './components/ProcessTimeline';
import ClientTestimonial from './components/ClientTestimonial';
import ProcessVisualization from './components/ProcessVisualization';
import MethodologyPanel from './components/MethodologyPanel';

const ProcessLaboratory = () => {
  const [activeWorkstation, setActiveWorkstation] = useState(0);
  const [activeProcess, setActiveProcess] = useState(0);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [expandedMethodology, setExpandedMethodology] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Mock data for workstations
  const workstations = [
    {
      id: 1,
      name: "Ideation Station",
      description: "Where concepts take shape",
      phase: "Discovery",
      icon: "Lightbulb",
      elements: [
        {
          name: "Mind Maps",
          icon: "GitBranch",
          type: "interactive",
          tooltip: "Explore interconnected ideas and concepts"
        },
        {
          name: "Concept Bubbles",
          icon: "Circle",
          type: "interactive",
          tooltip: "Click to reveal thinking processes"
        },
        {
          name: "User Personas",
          icon: "Users",
          type: "visualization",
          tooltip: "Target audience analysis"
        },
        {
          name: "Problem Definition",
          icon: "Target",
          type: "interactive",
          tooltip: "Core challenge identification"
        },
        {
          name: "Inspiration Board",
          icon: "Image",
          type: "visualization",
          tooltip: "Visual references and mood"
        },
        {
          name: "Brainstorm Notes",
          icon: "FileText",
          type: "interactive",
          tooltip: "Raw ideas and insights"
        }
      ],
      tools: ["Miro", "Figma", "Notion", "Whimsical", "Post-it Notes"],
      metrics: [
        { value: "50+", label: "Ideas Generated" },
        { value: "3-5", label: "Concepts Selected" }
      ]
    },
    {
      id: 2,
      name: "Prototyping Lab",
      description: "Rapid iteration and testing",
      phase: "Design",
      icon: "Layers",
      elements: [
        {
          name: "Wireframes",
          icon: "Layout",
          type: "interactive",
          tooltip: "Low-fidelity structure exploration"
        },
        {
          name: "Interactive Mockups",
          icon: "Smartphone",
          type: "interactive",
          tooltip: "High-fidelity design prototypes"
        },
        {
          name: "User Flows",
          icon: "GitBranch",
          type: "visualization",
          tooltip: "Journey mapping and navigation"
        },
        {
          name: "Component Library",
          icon: "Package",
          type: "visualization",
          tooltip: "Reusable design elements"
        },
        {
          name: "Usability Tests",
          icon: "TestTube",
          type: "interactive",
          tooltip: "User feedback and validation"
        },
        {
          name: "Design System",
          icon: "Grid",
          type: "visualization",
          tooltip: "Consistent design standards"
        }
      ],
      tools: ["Figma", "Sketch", "InVision", "Principle", "Framer"],
      metrics: [
        { value: "15+", label: "Iterations" },
        { value: "95%", label: "User Satisfaction" }
      ]
    },
    {
      id: 3,
      name: "Development Hub",
      description: "Code comes to life",
      phase: "Build",
      icon: "Code",
      elements: [
        {
          name: "Code Architecture",
          icon: "Database",
          type: "visualization",
          tooltip: "System structure and organization"
        },
        {
          name: "Component Building",
          icon: "Blocks",
          type: "interactive",
          tooltip: "Modular development approach"
        },
        {
          name: "API Integration",
          icon: "Plug",
          type: "visualization",
          tooltip: "Backend connectivity"
        },
        {
          name: "Testing Suite",
          icon: "CheckCircle",
          type: "interactive",
          tooltip: "Quality assurance processes"
        },
        {
          name: "Performance Optimization",
          icon: "Zap",
          type: "visualization",
          tooltip: "Speed and efficiency improvements"
        },
        {
          name: "Version Control",
          icon: "GitBranch",
          type: "interactive",
          tooltip: "Code management and collaboration"
        }
      ],
      tools: ["React", "Node.js", "Git", "Jest", "Webpack", "Docker"],
      metrics: [
        { value: "98%", label: "Test Coverage" },
        { value: "<2s", label: "Load Time" }
      ]
    },
    {
      id: 4,
      name: "Refinement Center",
      description: "Polish and perfection",
      phase: "Optimize",
      icon: "Sparkles",
      elements: [
        {
          name: "A/B Testing",
          icon: "BarChart",
          type: "interactive",
          tooltip: "Data-driven improvements"
        },
        {
          name: "Performance Metrics",
          icon: "Activity",
          type: "visualization",
          tooltip: "Real-world usage analytics"
        },
        {
          name: "User Feedback",
          icon: "MessageSquare",
          type: "interactive",
          tooltip: "Direct user insights"
        },
        {
          name: "Bug Fixes",
          icon: "Bug",
          type: "visualization",
          tooltip: "Issue resolution tracking"
        },
        {
          name: "Feature Enhancement",
          icon: "Plus",
          type: "interactive",
          tooltip: "Continuous improvement"
        },
        {
          name: "Documentation",
          icon: "Book",
          type: "visualization",
          tooltip: "Knowledge preservation"
        }
      ],
      tools: ["Google Analytics", "Hotjar", "Sentry", "Lighthouse", "Jira"],
      metrics: [
        { value: "25%", label: "Performance Boost" },
        { value: "4.8/5", label: "User Rating" }
      ]
    }
  ];

  // Mock data for process timeline
  const processes = [
    {
      name: "Research & Discovery",
      icon: "Search",
      description: "Understanding the problem space and user needs",
      steps: [
        {
          title: "Stakeholder Interviews",
          description: "Gather requirements and business objectives",
          duration: "1-2 weeks"
        },
        {
          title: "User Research",
          description: "Conduct surveys, interviews, and observations",
          duration: "2-3 weeks"
        },
        {
          title: "Competitive Analysis",
          description: "Study market landscape and best practices",
          duration: "1 week"
        },
        {
          title: "Problem Definition",
          description: "Synthesize findings into clear problem statement",
          duration: "3-5 days"
        }
      ]
    },
    {
      name: "Ideation & Concept",
      icon: "Lightbulb",
      description: "Generating and evaluating creative solutions",
      steps: [
        {
          title: "Brainstorming Sessions",
          description: "Generate diverse solution approaches",
          duration: "1 week"
        },
        {
          title: "Concept Development",
          description: "Refine promising ideas into concrete concepts",
          duration: "1-2 weeks"
        },
        {
          title: "Feasibility Assessment",
          description: "Evaluate technical and business viability",
          duration: "3-5 days"
        },
        {
          title: "Concept Selection",
          description: "Choose optimal solution for development",
          duration: "2-3 days"
        }
      ]
    },
    {
      name: "Design & Prototype",
      icon: "Palette",
      description: "Creating visual and interactive representations",
      steps: [
        {
          title: "Information Architecture",
          description: "Structure content and navigation flow",
          duration: "1 week"
        },
        {
          title: "Wireframing",
          description: "Create low-fidelity layout structures",
          duration: "1-2 weeks"
        },
        {
          title: "Visual Design",
          description: "Apply branding and aesthetic elements",
          duration: "2-3 weeks"
        },
        {
          title: "Interactive Prototype",
          description: "Build clickable, testable prototype",
          duration: "1-2 weeks"
        }
      ]
    },
    {
      name: "Development & Testing",
      icon: "Code",
      description: "Building and validating the final solution",
      steps: [
        {
          title: "Technical Architecture",
          description: "Plan system structure and technology stack",
          duration: "3-5 days"
        },
        {
          title: "Frontend Development",
          description: "Build user interface and interactions",
          duration: "4-6 weeks"
        },
        {
          title: "Backend Integration",
          description: "Connect APIs and database systems",
          duration: "2-3 weeks"
        },
        {
          title: "Quality Assurance",
          description: "Test functionality, performance, and usability",
          duration: "1-2 weeks"
        }
      ]
    }
  ];

  // Mock data for client testimonials
  const testimonials = [
    {
      id: 1,
      name: "Sarah Chen",
      role: "Product Manager",
      company: "TechFlow Solutions",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      project: "E-commerce Platform Redesign",
      projectDescription: "Complete overhaul of our online shopping experience with focus on mobile optimization and conversion rate improvement.",
      quote: "The systematic approach to understanding our users and iterating on solutions was impressive. Every decision was backed by research and testing, which gave us confidence in the final product.",
      duration: "3:42",
      rating: 5,
      date: "March 2024",
      processHighlights: [
        "Thorough user research phase",
        "Regular stakeholder check-ins",
        "Data-driven design decisions",
        "Comprehensive testing approach"
      ]
    },
    {
      id: 2,
      name: "Marcus Rodriguez",
      role: "Startup Founder",
      company: "GreenTech Innovations",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      project: "SaaS Dashboard Development",
      projectDescription: "Building an intuitive analytics dashboard for environmental monitoring with real-time data visualization.",
      quote: "What stood out was the methodical approach to complex data visualization. The process was transparent, collaborative, and resulted in a product that exceeded our expectations.",
      duration: "4:15",
      rating: 5,
      date: "February 2024",
      processHighlights: [
        "Agile development methodology",
        "Weekly progress demonstrations",
        "User-centered design approach",
        "Performance optimization focus"
      ]
    },
    {
      id: 3,
      name: "Emily Watson",
      role: "Creative Director",
      company: "Brand Studio Co.",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      project: "Interactive Brand Experience",
      projectDescription: "Creating an immersive web experience that showcases our agency\'s creative capabilities through 3D interactions.",
      quote: "The blend of creative vision and technical expertise was exactly what we needed. The process felt like a true partnership, with constant communication and iteration.",
      duration: "2:58",
      rating: 5,
      date: "January 2024",
      processHighlights: [
        "Creative concept development",
        "Technical feasibility planning",
        "Iterative design refinement",
        "Cross-browser compatibility testing"
      ]
    }
  ];

  // Mock data for process visualizations
  const visualizationData = {
    code: {
      codeSteps: [
        "// Initialize project structure",
        "// Set up component architecture", 
        "// Implement core functionality",
        "// Add interactive features",
        "// Optimize performance",
        "// Deploy to production"
      ],
      commits: 247,
      branches: 8,
      testCoverage: 94,
      tests: 156
    },
    design: {
      designStages: [
        {
          name: "Sketches",
          icon: "PenTool",
          description: "Initial concept exploration"
        },
        {
          name: "Wireframes", 
          icon: "Layout",
          description: "Structure and layout planning"
        },
        {
          name: "Mockups",
          icon: "Image",
          description: "Visual design application"
        },
        {
          name: "Prototype",
          icon: "Play",
          description: "Interactive validation"
        }
      ],
      iterations: 23
    },
    research: {
      researchMethods: [
        {
          name: "User Interviews",
          icon: "MessageCircle",
          participants: 12
        },
        {
          name: "Surveys",
          icon: "FileText", 
          participants: 156
        },
        {
          name: "Usability Testing",
          icon: "TestTube",
          participants: 8
        },
        {
          name: "Analytics Review",
          icon: "BarChart",
          participants: 1
        }
      ],
      insights: [
        "Users prefer visual navigation over text menus",
        "Mobile performance is critical for engagement", 
        "Onboarding flow needs simplification",
        "Social proof increases conversion rates"
      ]
    }
  };

  // Mock data for methodologies
  const methodologies = [
    {
      id: 1,
      title: "Design Thinking Framework",
      subtitle: "Human-centered approach to innovation",
      icon: "Heart",
      overview: "A methodology that focuses on understanding user needs, challenging assumptions, and creating innovative solutions through iterative processes. This approach ensures that every solution is grounded in real user problems and validated through testing.",
      frameworks: [
        {
          name: "Double Diamond",
          icon: "Diamond",
          description: "A visual representation of the design process that emphasizes divergent and convergent thinking phases.",
          steps: [
            {
              title: "Discover",
              description: "Explore the problem space through research and observation"
            },
            {
              title: "Define", 
              description: "Synthesize insights into a clear problem statement"
            },
            {
              title: "Develop",
              description: "Generate and iterate on potential solutions"
            },
            {
              title: "Deliver",
              description: "Test and refine the chosen solution"
            }
          ],
          tools: ["User Interviews", "Journey Mapping", "Personas", "Prototyping", "A/B Testing"],
          metrics: [
            { value: "85%", label: "User Satisfaction" },
            { value: "40%", label: "Faster Delivery" },
            { value: "60%", label: "Reduced Iterations" }
          ]
        },
        {
          name: "Lean UX",
          icon: "Zap",
          description: "A collaborative approach that focuses on the actual experience being designed rather than deliverables.",
          steps: [
            {
              title: "Assumptions",
              description: "Document business and user assumptions"
            },
            {
              title: "Hypotheses",
              description: "Create testable hypotheses from assumptions"
            },
            {
              title: "MVP",
              description: "Build minimum viable product to test hypotheses"
            },
            {
              title: "Learning",
              description: "Measure results and iterate based on feedback"
            }
          ],
          tools: ["Assumption Mapping", "Hypothesis Canvas", "MVP Development", "Analytics", "User Feedback"],
          metrics: [
            { value: "50%", label: "Faster Validation" },
            { value: "30%", label: "Cost Reduction" },
            { value: "90%", label: "Team Alignment" }
          ]
        }
      ]
    },
    {
      id: 2,
      title: "Agile Development Process",
      subtitle: "Iterative and collaborative software development",
      icon: "RefreshCw",
      overview: "An iterative approach to software development that emphasizes collaboration, flexibility, and rapid delivery of working software. This methodology allows for continuous improvement and adaptation to changing requirements.",
      frameworks: [
        {
          name: "Scrum",
          icon: "Users",
          description: "A framework that organizes work into time-boxed iterations called sprints, with defined roles and ceremonies.",
          steps: [
            {
              title: "Sprint Planning",
              description: "Define goals and select work for the upcoming sprint"
            },
            {
              title: "Daily Standups",
              description: "Brief daily meetings to sync progress and identify blockers"
            },
            {
              title: "Sprint Review",
              description: "Demonstrate completed work to stakeholders"
            },
            {
              title: "Retrospective",
              description: "Reflect on process and identify improvements"
            }
          ],
          tools: ["Jira", "Slack", "Git", "CI/CD Pipeline", "Testing Frameworks"],
          metrics: [
            { value: "2 weeks", label: "Sprint Length" },
            { value: "95%", label: "On-time Delivery" },
            { value: "4.7/5", label: "Team Satisfaction" }
          ]
        },
        {
          name: "Kanban",
          icon: "Trello",
          description: "A visual workflow management method that focuses on continuous delivery and limiting work in progress.",
          steps: [
            {
              title: "Visualize Work",
              description: "Create a visual board showing all work items"
            },
            {
              title: "Limit WIP",
              description: "Set limits on work in progress to improve flow"
            },
            {
              title: "Manage Flow",
              description: "Monitor and optimize the flow of work"
            },
            {
              title: "Continuous Improvement",
              description: "Regularly review and improve the process"
            }
          ],
          tools: ["Kanban Board", "Flow Metrics", "Cycle Time Analysis", "Cumulative Flow Diagram"],
          metrics: [
            { value: "3 days", label: "Avg Cycle Time" },
            { value: "98%", label: "Flow Efficiency" },
            { value: "25%", label: "Lead Time Reduction" }
          ]
        }
      ]
    }
  ];

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Auto-rotate testimonials
    const interval = setInterval(() => {
      setActiveTestimonial(prev => (prev + 1) % testimonials?.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [testimonials?.length]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mx-auto animate-pulse-glow">
            <Icon name="Beaker" size={32} className="text-accent" />
          </div>
          <div className="space-y-2">
            <h2 className="font-headline font-bold text-xl text-foreground">
              Initializing Laboratory
            </h2>
            <p className="font-body text-sm text-muted-foreground">
              Preparing interactive process environment...
            </p>
          </div>
          <div className="w-48 h-1 bg-border rounded-full mx-auto overflow-hidden">
            <div className="h-full bg-gradient-to-r from-accent to-cosmic-energy animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Process Laboratory - 3D Portfolio Showcase</title>
        <meta name="description" content="Explore the systematic creative process through an interactive 3D laboratory environment. Discover methodologies, workstations, and client testimonials." />
        <meta name="keywords" content="design process, development methodology, creative workflow, user experience, systematic approach" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-24 pb-16 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center space-y-6 mb-16">
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-accent/10 rounded-full border border-accent/20">
                <Icon name="Beaker" size={20} className="text-accent" />
                <span className="font-body font-medium text-sm text-accent">
                  Interactive Process Laboratory
                </span>
              </div>
              
              <h1 className="font-headline font-bold text-4xl md:text-6xl text-foreground">
                Where Methodology
                <span className="block text-accent">Meets Innovation</span>
              </h1>
              
              <p className="font-body text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                Step into an interactive laboratory where creative processes become tangible experiences. 
                Explore workstations, discover methodologies, and witness the systematic approach that 
                transforms ideas into exceptional digital solutions.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                <Button
                  variant="default"
                  size="lg"
                  iconName="Play"
                  iconPosition="left"
                  className="bg-accent text-background hover:bg-accent/90"
                >
                  Start Laboratory Tour
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Download"
                  iconPosition="left"
                  className="border-accent/30 text-accent hover:bg-accent/10"
                >
                  Download Process Guide
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Process Workstations */}
        <section className="py-16 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-headline font-bold text-3xl md:text-4xl text-foreground mb-4">
                Interactive Workstations
              </h2>
              <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
                Each workstation represents a phase of the creative process. Click to explore 
                interactive elements and discover the tools and techniques used.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {workstations?.map((workstation, index) => (
                <ProcessWorkstation
                  key={workstation?.id}
                  workstation={workstation}
                  isActive={activeWorkstation === index}
                  onActivate={() => setActiveWorkstation(index)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Process Timeline */}
        <section className="py-16 px-6 lg:px-8 bg-surface/20">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-headline font-bold text-3xl md:text-4xl text-foreground mb-4">
                Process Journey
              </h2>
              <p className="font-body text-lg text-muted-foreground">
                Navigate through the complete creative process timeline and explore each phase in detail.
              </p>
            </div>

            <ProcessTimeline
              processes={processes}
              activeProcess={activeProcess}
              onProcessChange={setActiveProcess}
            />
          </div>
        </section>

        {/* Process Visualizations */}
        <section className="py-16 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-headline font-bold text-3xl md:text-4xl text-foreground mb-4">
                Process Visualizations
              </h2>
              <p className="font-body text-lg text-muted-foreground">
                Interactive demonstrations of different process types and their unique characteristics.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <ProcessVisualization
                type="research"
                data={visualizationData?.research}
                isActive={true}
              />
              <ProcessVisualization
                type="design"
                data={visualizationData?.design}
                isActive={true}
              />
              <ProcessVisualization
                type="code"
                data={visualizationData?.code}
                isActive={true}
              />
            </div>
          </div>
        </section>

        {/* Client Testimonials */}
        <section className="py-16 px-6 lg:px-8 bg-surface/20">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-headline font-bold text-3xl md:text-4xl text-foreground mb-4">
                Client Process Experience
              </h2>
              <p className="font-body text-lg text-muted-foreground">
                Hear from clients about their experience with our systematic approach to creative problem-solving.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {testimonials?.map((testimonial, index) => (
                <ClientTestimonial
                  key={testimonial?.id}
                  testimonial={testimonial}
                  isActive={activeTestimonial === index}
                  onActivate={() => setActiveTestimonial(index)}
                />
              ))}
            </div>

            {/* Testimonial Navigation */}
            <div className="flex items-center justify-center space-x-2 mt-8">
              {testimonials?.map((_, index) => (
                <button
                  key={index}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    activeTestimonial === index
                      ? 'bg-accent scale-125' :'bg-border hover:bg-accent/50'
                  }`}
                  onClick={() => setActiveTestimonial(index)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Methodology Panels */}
        <section className="py-16 px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-headline font-bold text-3xl md:text-4xl text-foreground mb-4">
                Methodology Deep Dive
              </h2>
              <p className="font-body text-lg text-muted-foreground">
                Explore the frameworks and approaches that guide our creative process.
              </p>
            </div>

            <div className="space-y-6">
              {methodologies?.map((methodology) => (
                <MethodologyPanel
                  key={methodology?.id}
                  methodology={methodology}
                  isExpanded={expandedMethodology === methodology?.id}
                  onToggle={() => setExpandedMethodology(
                    expandedMethodology === methodology?.id ? null : methodology?.id
                  )}
                />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-6 lg:px-8 bg-gradient-to-r from-accent/10 to-cosmic-energy/10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="space-y-6">
              <h2 className="font-headline font-bold text-3xl md:text-4xl text-foreground">
                Ready to Experience This Process?
              </h2>
              <p className="font-body text-lg text-muted-foreground">
                Let's collaborate on your next project using these proven methodologies 
                and systematic approaches to create exceptional digital experiences.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                <Button
                  variant="default"
                  size="lg"
                  iconName="MessageCircle"
                  iconPosition="left"
                  className="bg-accent text-background hover:bg-accent/90"
                  onClick={() => window.location.href = '/contact-nexus'}
                >
                  Start Your Project
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Eye"
                  iconPosition="left"
                  className="border-accent/30 text-accent hover:bg-accent/10"
                  onClick={() => window.location.href = '/interactive-portfolio-gallery'}
                >
                  View Portfolio
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Floating Particles */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="particle animate-float" style={{ left: '5%', top: '20%', animationDelay: '0s' }}></div>
          <div className="particle animate-float" style={{ left: '15%', top: '60%', animationDelay: '2s' }}></div>
          <div className="particle animate-float" style={{ left: '25%', top: '40%', animationDelay: '4s' }}></div>
          <div className="particle animate-float" style={{ left: '35%', top: '80%', animationDelay: '6s' }}></div>
          <div className="particle animate-float" style={{ left: '45%', top: '30%', animationDelay: '8s' }}></div>
          <div className="particle animate-float" style={{ left: '55%', top: '70%', animationDelay: '10s' }}></div>
          <div className="particle animate-float" style={{ left: '65%', top: '50%', animationDelay: '12s' }}></div>
          <div className="particle animate-float" style={{ left: '75%', top: '25%', animationDelay: '14s' }}></div>
          <div className="particle animate-float" style={{ left: '85%', top: '65%', animationDelay: '16s' }}></div>
          <div className="particle animate-float" style={{ left: '95%', top: '45%', animationDelay: '18s' }}></div>
        </div>
      </div>
    </>
  );
};

export default ProcessLaboratory;